import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle2, Pencil, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { orpc } from '../../../apis/orpc-query';
import { useAuth } from '../../../auth/AuthContext';
import { Button } from '../../../components/ui/Button';
import { DefinitionList, type DefinitionItem, StatCard } from '../../../components/ui/DefinitionList';
import { Dialog } from '../../../components/ui/Dialog';
import { Input } from '../../../components/ui/Input';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusText } from '../../../components/ui/StatusText';
import { Tabs, type TabItem } from '../../../components/ui/Tabs';
import { formatDay, formatDayTime, formatKm, formatText } from '../../../lib/format';
import { serverMessage } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { RunFieldList } from '../RunFieldList';
import { formatClockTime, formatDuration, formatResult, outcomeTone, runConditionsSummary, runSourceTone, runStatusTone } from '../run-display';
import { CONDITION_GROUPS, isHybridPowertrain, MEASUREMENT_GROUPS } from '../run-fields';

type DetailTab = 'conditions' | 'measurements' | 'validity';

export const RunDetailPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { id = '' } = useParams<{ id: string }>();

  const [tab, setTab] = useState<DetailTab>('validity');
  const [isInvalidateOpen, setIsInvalidateOpen] = useState(false);
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const run = useQuery(orpc.runs.get.queryOptions({ input: { id }, enabled: id !== '' }));

  const submitRun = useMutation(orpc.runs.submit.mutationOptions());
  const revalidateRun = useMutation(orpc.runs.revalidate.mutationOptions());
  const invalidateRun = useMutation(orpc.runs.invalidate.mutationOptions());

  if (run.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner label={t('app.loading')} />
        {t('app.loading')}
      </div>
    );
  }

  if (run.isError || !run.data) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/runs')}
        >
          <ArrowLeft className="size-3.5" />
          {t('actions.back')}
        </Button>
        <div className="rounded-lg border border-border bg-card p-8">
          <p className="text-sm text-muted-foreground">{t('runs.notFound')}</p>
        </div>
      </div>
    );
  }

  const data = run.data;
  const visibility = { isHybrid: isHybridPowertrain(data.vehicle.powertrainType), isDrive: data.scenario.testType === 'DRIVE' };

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.runs.get.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.runs.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.benchmarks.coverageMatrix.key() }),
    ]);
  };

  const runAction = async (action: () => Promise<unknown>): Promise<void> => {
    setActionError(null);

    try {
      await action();
      await invalidate();
    } catch (error) {
      setActionError(serverMessage(error) ?? t('errors.saveFailed'));
    }
  };

  const onInvalidate = async (): Promise<void> => {
    await runAction(async () => {
      await invalidateRun.mutateAsync({ id: data.id, note });
      setIsInvalidateOpen(false);
      setNote('');
    });
  };

  const sessionItems: DefinitionItem[] = [
    { label: t('runs.detail.testDate'), value: formatDay(data.testDate) },
    { label: t('runs.detail.time'), value: `${formatClockTime(data.startTime)} – ${formatClockTime(data.endTime)}` },
    { label: t('runs.detail.duration'), value: formatDuration(data.measurements?.durationTotalSec) },
    {
      label: t('runs.detail.route'),
      value: data.route === null ? t('common.notSet') : `${data.route.name} (${data.route.code} v${data.route.version})`,
    },
    // The route's own vertical profile, exactly as the routes table and the report print it — and omitted
    // entirely for a route nobody has surveyed, rather than shown as an empty row.
    ...(data.route?.elevationProfile ? [{ label: t('runs.detail.elevation'), value: data.route.elevationProfile }] : []),
    // Traffic is a condition of this test, so it reads beside the route rather than inside it — and it is
    // omitted entirely when nothing was recorded, rather than shown as an empty row.
    ...(data.conditions?.trafficLevel ? [{ label: t('runs.detail.traffic'), value: t(`enums.trafficLevel.${data.conditions.trafficLevel}`) }] : []),
    { label: t('runs.detail.protocol'), value: `${data.protocol.code} v${data.protocol.version} — ${data.protocol.unit}` },
    { label: t('runs.detail.driver'), value: formatText(data.driverName) },
    { label: t('runs.detail.operator'), value: formatText(data.operatorName) },
    { label: t('runs.detail.campaign'), value: formatText(data.campaignTag) },
    {
      label: t('runs.detail.source'),
      value: (
        <StatusText
          label={t(`enums.runSource.${data.source}`)}
          tone={runSourceTone(data.source)}
        />
      ),
    },
    { label: t('runs.detail.recorded'), value: formatDayTime(data.createdAt) },
    { label: t('runs.detail.notes'), value: <span className="whitespace-pre-line">{formatText(data.notes)}</span> },
  ];

  const tabs: readonly TabItem<DetailTab>[] = [
    { value: 'validity', label: t('runs.detail.tabs.validity'), badge: data.validations.length },
    { value: 'conditions', label: t('runs.detail.tabs.conditions') },
    { value: 'measurements', label: t('runs.detail.tabs.measurements') },
  ];

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/runs')}
      >
        <ArrowLeft className="size-3.5" />
        {t('nav.runs')}
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-foreground">{data.vehicle.label}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-mono text-xs">{data.scenario.code}</span> · {runConditionsSummary(data, t)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {data.status === 'DRAFT' ? (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/runs/new?runId=${data.id}`)}
              >
                <Pencil className="size-4" />
                {t('runs.detail.actions.edit')}
              </Button>
              <Button
                isLoading={submitRun.isPending}
                onClick={() => void runAction(() => submitRun.mutateAsync({ id: data.id }))}
              >
                <CheckCircle2 className="size-4" />
                {t('runs.detail.actions.submit')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                isLoading={revalidateRun.isPending}
                onClick={() => void runAction(() => revalidateRun.mutateAsync({ id: data.id }))}
              >
                <RefreshCw className="size-4" />
                {t('runs.detail.actions.revalidate')}
              </Button>
              {user?.role === 'MANAGER' ? (
                <Button
                  variant="outline"
                  onClick={() => setIsInvalidateOpen(true)}
                >
                  <Ban className="size-4" />
                  {t('runs.detail.actions.invalidate')}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>

      {actionError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('runs.detail.status')}
          value={
            <StatusText
              label={t(`enums.runStatus.${data.status}`)}
              tone={runStatusTone(data.status)}
            />
          }
          hint={data.status === 'DRAFT' ? t('runs.detail.draftHint') : t('runs.detail.judgedHint', { count: data.validations.length })}
        />
        <StatCard
          label={t('runs.detail.result')}
          value={formatResult(data.result)}
          hint={data.result === null ? t('runs.detail.noResult') : `${data.result.metric} · ${data.result.calculationVersion}`}
        />
        <StatCard
          label={t('runs.fields.fuelConsumedL')}
          value={
            data.measurements?.fuelConsumedL === null || data.measurements === null ? t('common.notSet') : `${data.measurements.fuelConsumedL} L`
          }
          // The measured distance against the route's surveyed one — which a route may simply not have.
          hint={
            data.route === null || data.route.distanceKm === null
              ? formatKm(data.measurements?.distanceTotalKm)
              : `${formatKm(data.measurements?.distanceTotalKm)} / ${formatKm(data.route.distanceKm)}`
          }
        />
        <StatCard
          label={t('runs.fields.ambientTempAvgC')}
          value={
            data.measurements?.ambientTempAvgC === null || data.measurements === null ? t('common.notSet') : `${data.measurements.ambientTempAvgC} °C`
          }
          hint={t('runs.detail.sideMetric')}
        />
      </div>

      <section className="rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-medium text-card-foreground">{t('runs.detail.session')}</h2>
        <DefinitionList
          className="mt-2"
          items={sessionItems}
        />
      </section>

      <Tabs
        items={tabs}
        value={tab}
        onChange={setTab}
      />

      {tab === 'validity' ? (
        <div className="space-y-3">
          <p className="max-w-3xl text-sm text-muted-foreground">{t('runs.detail.validityIntro')}</p>

          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border">
                  {[
                    t('runs.detail.validity.rule'),
                    t('runs.detail.validity.expected'),
                    t('runs.detail.validity.actual'),
                    t('runs.detail.validity.outcome'),
                    t('runs.detail.validity.message'),
                  ].map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="whitespace-nowrap px-4 py-3 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.validations.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-12 text-center text-muted-foreground"
                    >
                      {t('runs.detail.validityEmpty')}
                    </td>
                  </tr>
                ) : (
                  data.validations.map((validation) => (
                    <tr
                      key={validation.id}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="px-4 py-3 text-start font-mono text-xs text-foreground">{validation.ruleKey}</td>
                      <td className="px-4 py-3 text-start text-muted-foreground">{formatText(validation.expected)}</td>
                      <td className="px-4 py-3 text-start font-medium text-foreground">{formatText(validation.actualValue)}</td>
                      <td className="px-4 py-3 text-start">
                        <StatusText
                          label={t(`enums.outcome.${validation.outcome}`)}
                          tone={outcomeTone(validation.outcome)}
                        />
                      </td>
                      <td className="px-4 py-3 text-start text-muted-foreground">{formatText(validation.message)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === 'conditions' ? (
        <RunFieldList
          groups={CONDITION_GROUPS}
          values={data.conditions}
          visibility={visibility}
          emptyMessage={t('runs.detail.conditionsEmpty')}
        />
      ) : null}

      {tab === 'measurements' ? (
        <RunFieldList
          groups={MEASUREMENT_GROUPS}
          values={data.measurements}
          visibility={visibility}
          emptyMessage={t('runs.detail.measurementsEmpty')}
        />
      ) : null}

      <Dialog
        isOpen={isInvalidateOpen}
        onClose={() => setIsInvalidateOpen(false)}
        title={t('runs.detail.invalidateDialog.title')}
        description={t('runs.detail.invalidateDialog.body')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsInvalidateOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={note.trim() === ''}
              isLoading={invalidateRun.isPending}
              onClick={() => void onInvalidate()}
            >
              {t('runs.detail.invalidateDialog.confirm')}
            </Button>
          </>
        }
      >
        <label
          htmlFor="invalidate-note"
          className="block text-sm font-medium text-card-foreground"
        >
          {t('runs.detail.invalidateDialog.note')}
        </label>
        <Input
          id="invalidate-note"
          className="mt-1.5"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t('runs.detail.invalidateDialog.notePlaceholder')}
        />
      </Dialog>
    </div>
  );
};
